import React, { useState } from "react";
import { SectionHeading } from "./SectionHeading";
import { Scissors, CalendarCheck, CreditCard, BellRing } from "lucide-react";
import { cn } from "@/lib/utils";
import { Reveal, RevealStagger, RevealItem } from "./Reveal";

const steps = [
  {
    icon: Scissors,
    title: "Pick a service",
    short: "Choose",
    description:
      "Clients browse your services with clear pricing and duration, then choose the right one in a single tap.",
    detail: { label: "Selected", value: "Signature Cut & Style · $85" },
  },
  {
    icon: CalendarCheck,
    title: "Choose a time",
    short: "Schedule",
    description:
      "Real-time availability shows only the slots that actually work for your roster — no double bookings, ever.",
    detail: { label: "Booked for", value: "Tue 14 May · 11:00am with Amara" },
  },
  {
    icon: CreditCard,
    title: "Pay a deposit",
    short: "Secure",
    description:
      "Secure the appointment with a deposit at checkout to protect your time and reduce no-shows.",
    detail: { label: "Deposit", value: "$25 paid · balance on arrival" },
  },
  {
    icon: BellRing,
    title: "Get reminded",
    short: "Remind",
    description:
      "Automatic SMS and email reminders keep clients on track, with one-tap reschedule if plans change.",
    detail: { label: "Reminder", value: "Sent 24h & 2h before" },
  },
];

export default function BookingFlow() {
  const [active, setActive] = useState(0);
  const step = steps[active];

  return (
    <section id="how-it-works" className="scroll-mt-16 px-4 py-6 sm:py-6">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <SectionHeading
            eyebrow="How it works"
            title="A booking experience your clients will love"
            description="Four effortless steps from discovery to a confirmed, paid appointment."
          />
        </Reveal>

        <div className="mt-12 grid gap-6 lg:grid-cols-2 lg:items-center">
          {/* steps list */}
          <RevealStagger className="flex flex-col gap-3">
            {steps.map((s, i) => (
              <RevealItem key={s.title}>
                <button
                  type="button"
                  onClick={() => setActive(i)}
                  className={cn(
                    "group flex w-full items-start gap-4 rounded-2xl border p-5 text-left transition-all duration-300",
                    active === i
                      ? "border-brand/40 bg-card soft-shadow"
                      : "border-border bg-card/60 hover:bg-card",
                  )}
                >
                  <div
                    className={cn(
                      "flex size-11 shrink-0 items-center justify-center rounded-xl border transition-colors",
                      active === i
                        ? "border-brand/40 bg-brand text-white"
                        : "border-border bg-secondary text-muted-foreground",
                    )}
                  >
                    <s.icon className="size-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-brand">
                        Step {i + 1}
                      </span>
                    </div>
                    <h3 className="mt-0.5 text-lg font-semibold text-foreground">
                      {s.title}
                    </h3>
                    <p
                      className={cn(
                        "mt-1 text-sm leading-relaxed text-muted-foreground transition-all",
                        active === i ? "block" : "hidden sm:line-clamp-1",
                      )}
                    >
                      {s.description}
                    </p>
                  </div>
                </button>
              </RevealItem>
            ))}
          </RevealStagger>

          {/* preview */}
          <Reveal delay={0.15} y={32}>
            <div className="glass relative overflow-hidden rounded-3xl p-8 lg:p-10 hidden lg:block">
              <div
                aria-hidden="true"
                className="absolute -right-16 -top-16 size-56 rounded-full bg-brand/15 blur-3xl"
              />
              <div className="relative">
                <div className="flex flex-wrap items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {steps.map((s, i) => (
                    <React.Fragment key={s.short}>
                      <button
                        type="button"
                        onClick={() => setActive(i)}
                        data-testid={`booking-flow-tab-${s.short.toLowerCase()}`}
                        className={cn(
                          "rounded-full px-3 py-1 transition-all",
                          i === active
                            ? "bg-brand/10 text-brand"
                            : "text-muted-foreground hover:text-brand hover:bg-brand/5",
                        )}
                      >
                        {s.short}
                      </button>
                      {i < steps.length - 1 && (
                        <span className="text-border">/</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>

                <div
                  key={active}
                  className="mt-8 [animation:slide-up-fade_0.4s_ease-out]"
                >
                  <div className="flex size-16 items-center justify-center rounded-2xl bg-brand text-white shadow-lg shadow-brand/30">
                    <step.icon className="size-7" />
                  </div>
                  <h3 className="mt-6 font-heading text-2xl font-bold text-foreground sm:text-3xl">
                    {step.title}
                  </h3>
                  <p className="mt-3 max-w-md text-pretty leading-relaxed text-muted-foreground">
                    {step.description}
                  </p>

                  <div className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {step.detail.label}
                    </p>
                    <p className="mt-1 text-lg font-medium text-foreground">
                      {step.detail.value}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
